import React from "react";

import { format } from "date-fns";
import { t } from "i18next";

import { Dot } from "@/components/atoms/Dot/Dot";

import { TProjectSummary } from "@/types";

const formatLastTask = (date: string | null) =>
	date ? format(new Date(date), "d MMM, HH:mm") : "—";

/**
 * The figures a project row shows after its name, in order: the client
 * (or "internal"), worked hours, task count, last task. A function of the
 * row: `t()` is i18next's plain export, so it runs at render, after
 * i18next.init(). `withClient` is off on a client's own page, where every
 * row would repeat the same name.
 */
export const PROJECT_LIST_METRICS = (
	project: TProjectSummary,
	{ withClient = true }: { withClient?: boolean } = {}
): React.ReactNode[] =>
	[
		!withClient ? null : project.client ? (
			<span
				key="client"
				className="inline-flex items-center gap-1.5"
				data-testid="project-client"
			>
				<Dot color={project.client.color} className="h-2 w-2 min-w-2" />
				{project.client.name}
			</span>
		) : (
			<span key="client" className="text-neutral-400">
				{t("projects.client.internalShort")}
			</span>
		),
		<span key="hours" className="font-medium text-neutral-800">
			{project.workedHours.str}
		</span>,
		t("projects.tasksCount", { count: project.tasksCount }),
		formatLastTask(project.lastTaskDate),
	].filter((metric) => null !== metric);
